/**
 * Enrollment Verification
 *
 * Flow:
 *  1. Student clicks "Request Enrollment Verification".
 *  2. A GET request is sent to our backend at /api/student/enrollment-verification.
 *     The backend validates the Laurus College account, passes the Azure AD Bearer
 *     token to the .ashx handler at smsbachelor.lauruscollege.edu, which reads the
 *     UPN claim to identify the student and returns the document.
 *  3. If the response is an image (image/*), it is displayed inline.
 *     If the response is a PDF, it is opened in a new tab (download as fallback).
 */

import { useState } from 'react';
import { getCurrentUser } from '../auth/LoginButton';
import axios from '../api/axios';

const ENROLLMENT_VERIFICATION_API_URL = '/student/enrollment-verification';

export function EnrollmentVerification() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const currentUser = getCurrentUser();

  // Allow any authenticated Laurus College user — the backend enforces that the
  // email exists in the SMS enrollment system (returning 404 if not found).
  // The "Student" Azure AD App Role is not required here because it is not
  // provisioned for all students.

  const handleRequestVerification = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Revoke any previous blob URL to avoid memory leaks
    setImageUrl(prev => { if (prev) window.URL.revokeObjectURL(prev); return null; });

    try {
      // Call backend proxy route (axios injects Authorization from sessionStorage)
      const response = await axios.get(ENROLLMENT_VERIFICATION_API_URL, {
        responseType: 'blob',
      });

      const blob = response.data as Blob;
      const contentType: string = blob.type || response.headers['content-type'] || '';
      const objectUrl = window.URL.createObjectURL(blob);

      if (contentType.startsWith('image/')) {
        // .ashx returned an image — display it inline
        setImageUrl(objectUrl);
      } else {
        // PDF or unknown — open in new tab, fall back to download
        const popup = window.open(objectUrl, '_blank', 'noopener,noreferrer');
        if (!popup) {
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = 'EnrollmentVerification.pdf';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
      setSuccess(true);
    } catch (err: unknown) {
      // When responseType='blob', axios wraps non-2xx bodies as Blobs.
      // Read the blob as text so we can surface the backend's error message.
      let message = 'An unexpected error occurred. Please try again.';
      const axiosErr = err as any;
      if (axiosErr?.response?.data instanceof Blob) {
        try {
          const text = await axiosErr.response.data.text();
          const parsed = JSON.parse(text);
          message = parsed?.message ?? message;
        } catch {
          // Blob wasn't JSON — fall back to the HTTP status text
          message = `Request failed (${axiosErr.response?.status ?? 'unknown'}). Please try again.`;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '680px', margin: '0 auto' }}>
      {/* Page header */}
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <h2 style={{
          margin: '0 0 0.5rem',
          fontSize: '26px',
          fontWeight: '700',
          color: 'var(--accent)',
        }}>
          📄 Enrollment Verification
        </h2>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
          Generate an official enrollment verification letter for {currentUser?.name ?? 'your account'}.
        </p>
      </div>

      {/* Card */}
      <div style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: '16px',
        padding: '36px 40px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      }}>
        <p style={{ color: 'var(--text-primary)', fontSize: '15px', lineHeight: '1.7', marginTop: 0 }}>
          Click the button below to request your enrollment verification document. The request
          is sent through the secure SIMS backend and the document will appear below.
        </p>

        <p style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.6' }}>
          <strong>Note:</strong> The document will include your current enrollment status and
          may take a few seconds to generate. Please do not close this page while the request
          is in progress.
        </p>

        {/* Action button */}
        <div style={{ marginTop: '28px', display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={handleRequestVerification}
            disabled={loading}
            style={{
              padding: '13px 32px',
              background: loading ? 'var(--card-border)' : 'var(--button-bg, #003366)',
              color: loading ? 'var(--text-secondary)' : 'var(--button-text, #fff)',
              border: 'none',
              borderRadius: '10px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              boxShadow: loading ? 'none' : '0 4px 12px rgba(0,0,0,0.25)',
              transition: 'all 0.25s',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {loading ? (
              <>
                <span style={{
                  width: '16px', height: '16px',
                  border: '2px solid var(--text-secondary)',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'spin 0.7s linear infinite',
                }} />
                Generating document…
              </>
            ) : (
              '📥 Request Enrollment Verification'
            )}
          </button>
        </div>

        {/* Feedback messages */}
        {success && !loading && !imageUrl && (
          <div style={{
            marginTop: '20px',
            padding: '12px 16px',
            background: 'rgba(46,160,67,0.12)',
            border: '1px solid rgba(46,160,67,0.35)',
            borderRadius: '8px',
            color: '#2ea043',
            fontSize: '14px',
          }}>
            ✅ Your enrollment verification document has been opened. If it did not appear,
            please check your browser's pop-up settings.
          </div>
        )}

        {imageUrl && (
          <div style={{ marginTop: '28px' }}>
            <img
              src={imageUrl}
              alt="Enrollment Verification"
              style={{ width: '100%', borderRadius: '8px', display: 'block' }}
            />
            <div style={{ marginTop: '12px', textAlign: 'center' }}>
              <a
                href={imageUrl}
                download="EnrollmentVerification"
                style={{
                  fontSize: '13px',
                  color: 'var(--accent)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                ⬇ Save a copy
              </a>
            </div>
          </div>
        )}

        {error && (
          <div style={{
            marginTop: '20px',
            padding: '12px 16px',
            background: 'rgba(220,53,69,0.1)',
            border: '1px solid rgba(220,53,69,0.35)',
            borderRadius: '8px',
            color: '#dc3545',
            fontSize: '14px',
          }}>
            ⚠️ {error}
          </div>
        )}
      </div>

      {/* Spinner keyframe */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
